(() => {
  const analytics = window.sytAnalytics || {};
  const dataLayer = (window.dataLayer = window.dataLayer || []);
  let lastCart = null;
  let checkoutPushed = false;

  const locale = () => analytics.locale || document.documentElement.lang || 'zh-TW';

  const money = (cents) => {
    const n = (Number(cents) || 0) / 100;
    return `HK$${n.toFixed(n % 1 ? 2 : 0)}`;
  };

  const pushEvent = (name, payload = {}) => {
    if (payload.ecommerce) dataLayer.push({ ecommerce: null });
    dataLayer.push({
      event: name,
      locale: locale(),
      ...payload,
    });
    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      window.Shopify.analytics.publish(name, payload);
    }
  };

  const gaItemFromLine = (line, extras = {}) => {
    if (!line) return null;
    const priceCents = Number(line.final_price != null ? line.final_price : line.price || 0);
    return {
      item_id: String(line.sku || line.variant_id || line.id || ''),
      item_name: line.product_title || line.title || '',
      item_brand: 'SYT Wellness',
      item_category: line.product_type || '',
      item_variant: line.variant_title && line.variant_title !== 'Default Title' ? line.variant_title : undefined,
      price: priceCents / 100,
      quantity: Number(extras.quantity != null ? extras.quantity : line.quantity || 1),
      index: extras.index != null ? extras.index : 0,
      item_list_name: extras.item_list_name || undefined,
    };
  };

  const gaItemFromEl = (el, extras = {}) => {
    if (!el) return null;
    const card = el.closest('[data-syt-item]') || el;
    const id = card.getAttribute('data-item-id') || card.getAttribute('data-variant-id');
    const name = card.getAttribute('data-item-name') || '';
    if (!id && !name) return null;
    return {
      item_id: String(id || ''),
      item_name: name,
      item_brand: card.getAttribute('data-item-brand') || 'SYT Wellness',
      item_category: card.getAttribute('data-item-category') || '',
      price: Number(card.getAttribute('data-item-price') || 0),
      quantity: 1,
      index: Number(card.getAttribute('data-item-index') || extras.index || 0),
      item_list_name: extras.item_list_name || undefined,
    };
  };

  const listNameFrom = (el) => {
    const list = el && el.closest('[data-syt-item-list]');
    return list ? list.getAttribute('data-syt-item-list') : undefined;
  };

  const itemsFromList = (list) => {
    const listName = list.getAttribute('data-syt-item-list');
    return [...list.querySelectorAll('[data-syt-item]')]
      .map((card, index) => gaItemFromEl(card, { item_list_name: listName, index }))
      .filter(Boolean);
  };

  const itemsFromCart = (cart) => (cart && cart.items ? cart.items.map((line, index) => gaItemFromLine(line, { index })) : []);

  const ecommerceFromCart = (cart) => ({
    currency: 'HKD',
    value: Number((cart && cart.total_price) || 0) / 100,
    items: itemsFromCart(cart),
  });

  const refreshFreeShipping = (cart) => {
    const bar = document.querySelector('[data-syt-fsbar]');
    if (!bar || !cart) return;
    const threshold = Number(bar.getAttribute('data-threshold') || 50000);
    const remaining = Math.max(0, threshold - cart.total_price);
    const pct = Math.min(100, (cart.total_price / threshold) * 100);
    const fill = bar.querySelector('[data-syt-fsbar-fill]');
    const text = bar.querySelector('[data-syt-fsbar-text]');
    if (fill) fill.style.width = `${pct}%`;
    if (text) {
      text.textContent = remaining === 0
        ? '已達免運費門檻'
        : `仲差 ${money(remaining)} 即享免運費`;
    }
  };

  const openCart = () => {
    document.dispatchEvent(new CustomEvent('syt:open-cart'));
    if (window.ajaxCart && typeof window.ajaxCart.load === 'function') {
      window.ajaxCart.load();
    }
    if (window.theme && typeof window.theme.mfpOpen === 'function') {
      setTimeout(() => window.theme.mfpOpen('cart'), 100);
      return;
    }
    const drawer = document.querySelector('#CartDrawer, .ajaxcart, .js-ajax-cart, [data-cart-drawer]');
    if (drawer) drawer.classList.add('is-open', 'active');
    else window.location.href = '/cart';
  };

  const addToCart = async ({ variantId, quantity = 1, properties, items, redirectToCart }) => {
    const body = items
      ? { items }
      : { id: Number(variantId), quantity, properties: properties || {} };
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      const message = json.description || json.message || '未能加入購物車';
      throw new Error(message);
    }
    const cartRes = await fetch('/cart.js');
    const cart = await cartRes.json();
    lastCart = cart;
    refreshFreeShipping(cart);
    if (redirectToCart) {
      window.location.href = (window.theme && theme.routes && theme.routes.cartUrl) || '/cart';
      return { json, cart };
    }
    openCart();
    return { json, cart };
  };

  const loadCart = () => fetch('/cart.js')
    .then((res) => res.json())
    .then((cart) => {
      lastCart = cart;
      refreshFreeShipping(cart);
      return cart;
    })
    .catch(() => lastCart);

  const fireBeginCheckout = async () => {
    if (checkoutPushed) return;
    checkoutPushed = true;
    const cart = await loadCart();
    pushEvent('begin_checkout', { ecommerce: ecommerceFromCart(cart || { items: [], total_price: 0 }) });
  };

  const isViewCartLink = (el) => {
    if (!el || el.tagName !== 'A') return false;
    const href = el.getAttribute('href') || '';
    return /\/cart\/?(\?|$|#)/.test(href) && !/checkout/.test(href);
  };

  document.addEventListener('click', async (event) => {
    const atc = event.target.closest('[data-syt-add-to-cart]');
    if (atc) {
      event.preventDefault();
      if (atc.disabled) return;
      try {
        atc.disabled = true;
        await addToCart({ variantId: atc.dataset.variantId });
        const item = gaItemFromEl(atc, { item_list_name: listNameFrom(atc) }) || {
          item_id: atc.dataset.itemId || atc.dataset.variantId,
          item_name: atc.dataset.itemName || '',
          item_brand: 'SYT Wellness',
          price: Number(atc.dataset.itemPrice || 0),
          quantity: 1,
          item_list_name: listNameFrom(atc),
        };
        pushEvent('add_to_cart', {
          ecommerce: { currency: 'HKD', value: Number(item.price || 0), items: [item] },
        });
      } catch (err) {
        atc.setAttribute('aria-live', 'polite');
        atc.textContent = err.message;
      } finally {
        atc.disabled = false;
      }
      return;
    }

    const bundle = event.target.closest('[data-syt-add-bundle]');
    if (bundle) {
      event.preventDefault();
      if (bundle.getAttribute('aria-busy') === 'true') return;
      const root = bundle.closest('[data-syt-bundle]');
      const cards = root ? [...root.querySelectorAll('[data-syt-item], [data-variant-id]')] : [];
      const seen = new Set();
      const ids = [];
      const gaItems = [];
      cards.forEach((el, index) => {
        const id = el.getAttribute('data-variant-id');
        if (!id || Number(id) <= 0 || seen.has(id)) return;
        seen.add(id);
        ids.push(id);
        gaItems.push(gaItemFromEl(el, { item_list_name: 'bundle_builder', index }) || {
          item_id: id,
          item_name: '',
          item_brand: 'SYT Wellness',
          quantity: 1,
          index,
          item_list_name: 'bundle_builder',
        });
      });
      if (!ids.length) return;
      const bundleId = bundle.getAttribute('data-bundle-id') || 'kit';
      const items = ids.map((id) => ({
        id: Number(id),
        quantity: 1,
        properties: { _bundle_id: bundleId },
      }));
      try {
        bundle.setAttribute('aria-busy', 'true');
        bundle.disabled = true;
        await addToCart({ items, redirectToCart: true });
        const value = gaItems.reduce((sum, item) => sum + Number(item.price || 0), 0);
        pushEvent('add_to_cart', { ecommerce: { currency: 'HKD', value, items: gaItems } });
      } catch (err) {
        bundle.disabled = false;
        bundle.removeAttribute('aria-busy');
        bundle.textContent = err.message;
      }
      return;
    }

    const remove = event.target.closest('.cart-item__remove, a[href*="quantity=0"]');
    if (remove && remove.matches('a')) {
      const href = remove.getAttribute('href') || '';
      const lineMatch = href.match(/[?&]line=(\d+)/);
      const line = lineMatch ? Number(lineMatch[1]) : 0;
      const item = lastCart && lastCart.items && line ? lastCart.items[line - 1] : null;
      if (item) {
        pushEvent('remove_from_cart', {
          ecommerce: { currency: 'HKD', items: [gaItemFromLine(item)] },
        });
      }
      return;
    }

    const productLink = event.target.closest('a[href*="/products/"]');
    if (productLink && !event.target.closest('[data-syt-add-to-cart], [data-syt-add-bundle]')) {
      const list = productLink.closest('[data-syt-item-list]');
      if (list) {
        const item = gaItemFromEl(productLink, { item_list_name: list.getAttribute('data-syt-item-list') });
        if (item) {
          pushEvent('select_item', { ecommerce: { currency: 'HKD', items: [item] } });
        }
      }
    }

    const checkout = event.target.closest('[name="checkout"], .cart__checkout, button.ajaxcart__checkout');
    if (checkout && !isViewCartLink(checkout)) {
      fireBeginCheckout();
    }
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (form && form.matches('[data-syt-bundle-form]')) {
      event.preventDefault();
      const button = form.querySelector('[data-syt-add-bundle]');
      if (button && button.getAttribute('aria-busy') !== 'true') button.click();
      return;
    }
    if (form && form.querySelector('[name="checkout"], .cart__checkout, button.ajaxcart__checkout')) {
      fireBeginCheckout();
    }
  }, true);

  const observeLists = () => {
    const nodes = document.querySelectorAll('[data-syt-item-list]');
    if (!nodes.length || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        if (entry.target.dataset.sytViewed) return;
        entry.target.dataset.sytViewed = '1';
        const listName = entry.target.getAttribute('data-syt-item-list');
        pushEvent('view_item_list', {
          ecommerce: {
            item_list_id: listName,
            item_list_name: listName,
            items: itemsFromList(entry.target),
          },
        });
        io.unobserve(entry.target);
      });
    }, { threshold: 0.4 });
    nodes.forEach((node) => io.observe(node));
  };

  const bindAjaxCart = () => {
    if (!window.jQuery) return;
    const $body = window.jQuery(document.body);
    $body.on('afterAddItem.ajaxCart', (_e, lineItem) => {
      const item = gaItemFromLine(lineItem);
      pushEvent('add_to_cart', {
        ecommerce: {
          currency: 'HKD',
          value: item ? item.price * item.quantity : 0,
          items: item ? [item] : [],
        },
      });
      loadCart();
    });
    $body.on('afterChangeItem.ajaxCart', (_e, line, quantity, cart) => {
      const prev = lastCart && lastCart.items ? lastCart.items[Number(line) - 1] : null;
      const qty = Number(quantity);
      if (prev && qty === 0) {
        pushEvent('remove_from_cart', {
          ecommerce: { currency: 'HKD', items: [gaItemFromLine(prev)] },
        });
      } else if (prev && qty < prev.quantity) {
        pushEvent('remove_from_cart', {
          ecommerce: { currency: 'HKD', items: [gaItemFromLine(prev, { quantity: prev.quantity - qty })] },
        });
      }
      lastCart = cart;
      refreshFreeShipping(cart);
    });
  };

  loadCart();
  observeLists();
  bindAjaxCart();
})();
